'use client';

import { createContext, useContext } from 'react';
import type {
  FindingAssetItem,
  GroupCheckboxState,
  InternalEnrichedFinding,
} from '@terra/ui-design-system';
import type { FindingStatusType } from '@terra/shared-schemas-orchestrator';

export type FindingsRowActions = {
  onEdit: (finding: InternalEnrichedFinding) => void;
  onDelete: (finding: InternalEnrichedFinding) => void;
  onChangeStatus: (
    finding: InternalEnrichedFinding,
    status: FindingStatusType,
  ) => Promise<void>;
  onTogglePublish: (finding: InternalEnrichedFinding) => Promise<void>;
  onGenerateReport: (finding: InternalEnrichedFinding) => void;
};

export type FindingsTableContextValue = {
  headerCheckboxState: GroupCheckboxState;
  hasLoadedActionableFindings: boolean;
  hasBulkSelection: boolean;
  collapsedGroupKeys: ReadonlySet<string>;
  rowActions: FindingsRowActions;
  isRowSelected: (findingId: string) => boolean;
  onSetFindingIdsSelected: ({
    ids,
    selected,
  }: {
    ids: readonly string[];
    selected: boolean;
  }) => void;
  onSelectAllLoaded: (selected: boolean) => void;
  onToggleGroupCollapsed: (groupKey: string) => void;
  onOpenAsset: (asset: FindingAssetItem) => void;
  onOpenSignal: ({
    signalId,
    title,
  }: {
    signalId: string;
    title: string | null;
  }) => void;
};

const FindingsTableContext = createContext<FindingsTableContextValue | null>(
  null,
);

export const FindingsTableContextProvider = FindingsTableContext.Provider;

export function useFindingsTableContext(): FindingsTableContextValue {
  const value = useContext(FindingsTableContext);

  if (!value) {
    throw new Error(
      'useFindingsTableContext must be used inside FindingsTableContextProvider',
    );
  }

  return value;
}
